/**
 * Queue + cast mode -> SpellSpec (DESIGN 2.4 resolution matrix, 2.5 numbers,
 * 2.6 self-casts) and the HUD names of DESIGN 6.5 / 9.
 */

import type { ConceptElement, Element, WovenElement } from './elements.js';
import { ALL_ELEMENTS, FORM_OF, PRIORITY, isConcept, isWoven } from './elements.js';
import type { DamagePacket, ElementCounts } from './damage.js';
import type { StatusId } from './status.js';
import { DEATH_MARK } from './weaves.js';
import {
  SPRAY_LIGHTNING_MUL, WATER_KNOCKBACK, addPackets, countElements, elementPacket, emptyPacket, packetElements,
  scalePacket, stackFluid, statusesOnly, sumPackets,
} from './damage.js';
import {
  ARC, ARMOR, AURA, BARRIER, BEAM, BOULDER, BUBBLE, MELEE, MINES, NOVA, QUAKE, SELF_CAST, SHARDS, SPRAY, STORM, WALL, WIND, LAVA_POOL,
} from './constants.js';
import { DEG } from './math.js';

/** A burning ground patch a Lava boulder leaves where it lands. */
export interface PoolSpec { el: Element; radius: number; lifetime: number; perTick: DamagePacket; tickSeconds: number }

export type CastMode = 'force'|'area'|'self'|'weapon';

/** Runtime list of cast modes (for validation and HUD previews). */
export const CAST_MODES: readonly CastMode[] = ['force', 'area', 'self', 'weapon'];

/**
 * Units: distances in arena units, `lifetime` in seconds, `ticks` in damage
 * ticks (10 Hz), angles (`halfAngle`) in RADIANS like every runtime angle
 * (math.ts conventions; only constants named *Deg are degrees). `elements`
 * is the queue sorted, so permutations of a queue yield deep-equal specs.
 *
 * Projectile: `direct` is the hit damage of the boulder, or of each shard
 * (`payload` is the whole barrage's and is split across `shards` by the
 * server). Self: `hotTicks` heals of `heal` each, spread evenly over
 * SELF_CAST.hotSeconds after the instant `heal`. Melee: `knockdown` mirrors
 * a knockdown in `packet.statuses` for the fissure.
 */
/**
 * A woven piece's signature on its Force body (docs/spells-depth.md 2): Flux drags
 * and steals speed, Ether passes walls and barriers, Dragonfire scorches the
 * ground, Rime freezes the dry, Plasma pops burning targets, Obsidian shatters
 * into shards, Storm Glass webs its shards with lightning. Absent on every
 * spell without a woven piece.
 * Slice 2: Wildfire's burning spreads (`spread`), Inferno's spreads and
 * scorches (`inferno`), the Soul beam heals friends and drains enemies
 * (`soul`), Pulse's nova thaws (`thaw`), Soul's nova finds only your side
 * (`allies`), and Void's ring unmakes what it touches (`unmake`).
 */
export type Sig = 'drag'|'phase'|'scorch'|'rime'|'pop'|'shatter'|'web'|'spread'|'inferno'|'soul'|'thaw'|'allies'|'unmake';

/**
 * Fields (woven pieces): a zone `dist` ahead (0 = on the caster) for
 * `lifetime` s. `scale` is the time scale inside (1 = none; their caster is
 * never slowed). `stasis` also stops projectiles; `rain` wets and douses;
 * `stop` holds everything still; `thunder` strikes the enemy under it; `slow` is Time.
 * Slice 2: `pull` drags bodies to its middle (Whirlpool); `flood` soaks and
 * lets lightning run through it (Deluge); `winter` chills everyone inside but
 * its caster each pulse; `blaze` burns everyone inside (Inferno on yourself);
 * `star` swells and bursts (Supernova). Fields with a `speed` roll away from
 * the caster and land `hit` once on each body they reach: `wave` carries
 * bodies along (Tide), `orb` eats missiles and hazards (Void), `roll` crushes
 * and freezes (Glacier).
 */
export type FieldSig = 'stasis'|'rain'|'stop'|'thunder'|'slow'|'wave'|'orb'|'roll'|'pull'|'flood'|'winter'|'blaze'|'star';

export type SpellSpec =
  | { kind:'beam'; ticks:number; perTick:DamagePacket; knockback:number; elements:Element[]; sig?:Sig }
  | { kind:'spray'; ticks:number; perTick:DamagePacket; range:number; halfAngle:number; knockback:number; elements:Element[]; sig?:Sig }
  | { kind:'arc'; ticks:number; perTick:DamagePacket; range:number; chain:number; elements:Element[]; sig?:Sig }
  | { kind:'projectile'; solid:'earth'|'ice'; shards:number; direct:number; payload:DamagePacket; blastRadius:number; speed:number; range:number; elements:Element[]; pool?:PoolSpec; sig?:Sig }
  | { kind:'nova'; radius:number; packet:DamagePacket; knockback:number; elements:Element[]; sig?:Sig }
  | { kind:'field'; sig:FieldSig; dist:number; radius:number; lifetime:number; scale:number; perTick:DamagePacket; elements:Element[]; speed?:number; hit?:DamagePacket }
  | { kind:'blink'; dist:number }
  | { kind:'quake'; radius:number; payload:DamagePacket; sig?:Sig }
  | { kind:'barrier'; geometry:'arc'|'ring'|'line'; hp:number }
  | { kind:'wall'; geometry:'arc'|'ring'|'line'; solid:'earth'|'ice'; hp:number; lifetime:number; rise:DamagePacket }
  | { kind:'mines'; geometry:'arc'|'ring'|'line'; count:number; packet:DamagePacket; knockback:number }
  | { kind:'storm'; geometry:'arc'|'ring'|'line'; lifetime:number; perTick:DamagePacket }
  /** `ward`: the next killing blow within `t` s leaves you at `hp` (Pulse, Soul). `cleanse`: statuses washed off first. */
  | { kind:'self'; packet:DamagePacket; heal:number; hotTicks:number; knockdown:boolean; ward?:{ t:number; hp:number }; cleanse?:'all'|StatusId[] }
  /** The Sun concept's Force cast: a burst `dist` ahead. */
  | { kind:'burst'; dist:number; radius:number; packet:DamagePacket; elements:Element[] }
  /** The Death concept: a mark on the first body along the aim, on everyone round you, or on you; it falls after `delay` s. */
  | { kind:'mark'; reach:'aim'|'around'|'self'; range:number; frac:number; bossFrac:number; delay:number }
  | { kind:'bubble'; hp:number }
  | { kind:'aura'; immune:Element[]; radius:number; lifetime:number }
  | { kind:'armor'; solid:'earth'|'ice'; absorb:number; slow:number; lifetime:number }
  | { kind:'melee'; shape:'swing'|'thrust'|'fissure'|'slash'; packet:DamagePacket; knockdown:boolean }
  | { kind:'wind'; geometry:'cone'|'ring'; knockback:number }
  | { kind:'none' };

export type SpellKind = SpellSpec['kind'];

type Geometry = 'arc'|'ring'|'line';

/** Force / Area / Weapon place hazards as an arc / ring / line (DESIGN 2.4). */
const GEOMETRY: Readonly<Record<Exclude<CastMode, 'self'>, Geometry>> = { force: 'arc', area: 'ring', weapon: 'line' };

/** Storm per-tick damage by element (DESIGN 2.5 Storm row); statuses come from the element. */
// Lava burns hotter than fire; a Spring storm heals whoever stands in it (enemies too); Mud is a swamp (statuses only).
const STORM_DAMAGE: Readonly<Partial<Record<Element, number>>> = { fire: STORM.fire, lightning: STORM.lightning, steam: STORM.steam, lava: STORM.fire * 1.5, spring: 30 };

const ORDER: Readonly<Record<Element, number>> = Object.fromEntries(ALL_ELEMENTS.map((el, i) => [el, i])) as Record<Element, number>;

/** Queue sorted in keyboard order (Q W E R A S D F, then steam, ice), so permutations deep-equal. */
function sortedElements(queue: readonly Element[]): Element[] {
  return [...queue].sort((a, b) => ORDER[a] - ORDER[b]);
}

function count(counts: ElementCounts, el: Element): number {
  return counts[el] ?? 0;
}

function hasKnockdown(p: DamagePacket): boolean {
  return p.statuses.includes('knockdown');
}

/**
 * The element with the highest PRIORITY in the queue (decides the form); null for an empty queue.
 * A tie goes to keyboard order (Earth over Lava, Water over Fire and Cold), never to queue order,
 * because element order must not change a spell (DESIGN 2.2 consequences).
 */
export function dominant(queue: readonly Element[]): Element | null {
  let best: Element | null = null;
  for (const el of queue) {
    if (best === null || PRIORITY[el] > PRIORITY[best] || (PRIORITY[el] === PRIORITY[best] && ORDER[el] < ORDER[best])) best = el;
  }
  return best;
}

/**
 * Resolve what a queue does in a cast mode (DESIGN 2.4). Pure: the same
 * queue (in any element order) and mode always yield a deep-equal spec, with
 * `elements` sorted.
 */
export function resolveCast(queue: readonly Element[], mode: CastMode): SpellSpec {
  const spec = resolveCastInner(queue, mode);
  // Specs for the newer fusions are named from the queue, not guessed back from their damage packet.
  if (queue.some((el) => el === 'mud' || el === 'lava' || el === 'spring' || isWoven(el))) QUEUE_OF.set(spec, sortedElements(queue));
  return spec;
}

/** Queue each fusion spec came from, for spellName (not serialized; client previews only). */
const QUEUE_OF = new WeakMap<SpellSpec, Element[]>();

function resolveCastInner(queue: readonly Element[], mode: CastMode): SpellSpec {
  const dom = dominant(queue);
  const counts = countElements(queue);
  const elements = sortedElements(queue);
  if (dom === null) return resolveEmpty(mode);
  if (dom === 'shield') return resolveShield(mode, counts, elements);
  if (isWoven(dom)) return resolveWoven(dom, mode, counts, elements);
  switch (mode) {
    case 'self': return resolveSelf(dom, counts);
    case 'weapon': return resolveWeapon(dom, counts);
    case 'area': return resolveArea(dom, counts, elements);
    case 'force': return resolveForce(dom, counts, elements);
  }
}

// ---------------------------------------------------------------- empty queue (DESIGN 2.4 last row)

function resolveEmpty(mode: CastMode): SpellSpec {
  switch (mode) {
    case 'force': return { kind: 'wind', geometry: 'cone', knockback: WIND.knockback };
    case 'area': return { kind: 'wind', geometry: 'ring', knockback: WIND.knockback };
    case 'self': return { kind: 'none' };
    case 'weapon': return { kind: 'melee', shape: 'swing', packet: meleeBase(), knockdown: false };
  }
}

function meleeBase(): DamagePacket {
  return { ...emptyPacket(), physical: MELEE.baseDamage };
}

// ---------------------------------------------------------------- shield rows (DESIGN 2.4 rows 1-4)

function resolveShield(mode: CastMode, counts: ElementCounts, elements: Element[]): SpellSpec {
  // Lava is rock too: E D F (Shield + Lava) still raises a wall, one that burns on the way up. So is glass,
  // and a Glacier is ice.
  const nEarth = count(counts, 'earth') + count(counts, 'lava') + count(counts, 'obsidian') + count(counts, 'stormglass');
  const nIce = count(counts, 'ice') + count(counts, 'glacier');
  const payloadElements = elements.filter((el) => el !== 'shield');
  const nTotal = elements.length;

  if (nEarth + nIce > 0) {
    // Earth outranks Ice as the solid; the other one rides along as payload.
    const solid: 'earth'|'ice' = nEarth > 0 ? 'earth' : 'ice';
    if (mode === 'self') {
      return solid === 'earth'
        ? { kind: 'armor', solid, absorb: ARMOR.rockAbsorb, slow: ARMOR.rockSlow, lifetime: ARMOR.lifetime }
        : { kind: 'armor', solid, absorb: ARMOR.iceAbsorb, slow: ARMOR.iceSlow, lifetime: ARMOR.lifetime };
    }
    return wallSpec(GEOMETRY[mode], solid, counts);
  }

  if (mode === 'self') {
    if (payloadElements.length === 0) return { kind: 'bubble', hp: BUBBLE.hp };
    return {
      kind: 'aura',
      immune: payloadElements.filter((el, i) => payloadElements.indexOf(el) === i),
      radius: AURA.baseRadius + nTotal,
      lifetime: AURA.baseLifetime + AURA.lifetimePerExtraElement * (nTotal - 2),
    };
  }

  const geometry = GEOMETRY[mode];
  // Void with Shield: a wall that blocks spells, not bodies (a barrier twice as tough).
  if (count(counts, 'void') > 0) return { kind: 'barrier', geometry, hp: BARRIER.hp * 2 };
  if (count(counts, 'life') + count(counts, 'arcane') + count(counts, 'pulse') + count(counts, 'soul') > 0) return minesSpec(geometry, counts);
  if (payloadElements.length > 0) return stormSpec(geometry, counts, payloadElements.length);
  return { kind: 'barrier', geometry, hp: BARRIER.hp };
}

function wallSpec(geometry: Geometry, solid: 'earth'|'ice', counts: ElementCounts): SpellSpec {
  const nEarth = count(counts, 'earth') + count(counts, 'lava') + count(counts, 'obsidian') + count(counts, 'stormglass');
  // A Glacier wall is a mountain of ice: it counts as four Ice for the wall's toughness.
  const nIce = count(counts, 'ice') + 4 * count(counts, 'glacier');
  const payload = sumPackets('payload', counts, ['shield', solid, 'glacier']);
  if (solid === 'earth') {
    // DESIGN 2.5 Wall: Earth rise is a 0-damage knockdown plus the payload.
    const rise = addPackets(statusesOnly(elementPacket('payload', 'earth', nEarth)), payload);
    return { kind: 'wall', geometry, solid, hp: WALL.earthHpBase + WALL.earthHpPerEarth * nEarth, lifetime: WALL.earthLifetime, rise };
  }
  // DESIGN 2.5 Wall: Ice rise is 275 x nIce physical (not the n^2 boulder payload) and chills.
  const iceRise: DamagePacket = { ...statusesOnly(elementPacket('payload', 'ice', nIce)), physical: WALL.iceRisePerIce * nIce };
  return { kind: 'wall', geometry, solid, hp: WALL.iceHpBase + WALL.iceHpPerIce * nIce, lifetime: WALL.iceLifetime, rise: addPackets(iceRise, payload) };
}

function minesSpec(geometry: Geometry, counts: ElementCounts): SpellSpec {
  const arcane = count(counts, 'arcane') > 0;
  // Pulse and Soul lay healing mines, like Life.
  const life = count(counts, 'life') + count(counts, 'pulse') + count(counts, 'soul') > 0;
  // DESIGN 2.5 Mines: Arcane 225 flat + launch, Life heals 599 flat, fluids add their projectile-payload values.
  const base: DamagePacket = { ...emptyPacket(), arcane: arcane ? MINES.arcaneDamage : 0, life: life ? MINES.lifeHeal : 0 };
  const packet = addPackets(base, sumPackets('payload', counts, ['shield', 'arcane', 'life']));
  const countByGeometry = { arc: MINES.countArc, ring: MINES.countRing, line: MINES.countLine } as const;
  return { kind: 'mines', geometry, count: countByGeometry[geometry], packet, knockback: arcane ? MINES.arcaneKnockback : 0 };
}

function stormSpec(geometry: Geometry, counts: ElementCounts, nPayload: number): SpellSpec {
  let perTick = emptyPacket();
  for (const el of ALL_ELEMENTS) {
    const n = count(counts, el);
    if (n === 0 || el === 'shield') continue;
    // A woven piece in a storm deals its spray numbers each storm tick, with its statuses.
    if (isWoven(el)) { perTick = addPackets(perTick, elementPacket('spray', el, n)); continue; }
    // Storm numbers scale by s(n) like every other fluid damage (DESIGN 2.5 lists the n = 1 values).
    const damage = (STORM_DAMAGE[el] ?? 0) * stackFluid(n);
    const p = statusesOnly(elementPacket('payload', el, n));
    if (el === 'fire') p.fire = damage;
    if (el === 'lightning') p.lightning = damage;
    if (el === 'steam') p.steam = damage;
    if (el === 'lava') p.fire += damage;
    if (el === 'spring') p.life += damage;
    perTick = addPackets(perTick, p);
  }
  // DESIGN 3 #33: E A -> 10 s, so the duration counts payload elements only.
  return { kind: 'storm', geometry, lifetime: STORM.baseLifetime + STORM.lifetimePerElement * nPayload, perTick };
}

// ---------------------------------------------------------------- Force (DESIGN 2.4 column 2)

function resolveForce(dom: Element, counts: ElementCounts, elements: Element[]): SpellSpec {
  const nWater = count(counts, 'water');
  switch (dom) {
    case 'lava':
    case 'earth': {
      // Lava is molten earth: it counts toward the rock (direct hit, knockdown) and its fire rides the blast.
      const nLava = count(counts, 'lava');
      const nEarth = count(counts, 'earth') + nLava;
      const nIce = count(counts, 'ice');
      const earth = elementPacket('payload', 'earth', nEarth);
      const ice = elementPacket('payload', 'ice', nIce);
      // DESIGN 3 #17: Ice on a boulder is direct hit damage, so a hailstone boulder reads as one big number.
      const payload = addPackets(addPackets(statusesOnly(earth), statusesOnly(ice)), sumPackets('payload', counts, ['earth', 'ice']));
      const beamBonus = count(counts, 'arcane') + count(counts, 'life') > 0 ? BOULDER.beamElementBlastBonus : 0;
      const spec: SpellSpec = {
        kind: 'projectile', solid: 'earth', shards: 0, direct: earth.physical + ice.physical, payload,
        blastRadius: BOULDER.blastRadius + BOULDER.waterBlastBonus * nWater + beamBonus,
        speed: BOULDER.speed, range: BOULDER.range, elements,
      };
      if (nLava > 0) {
        spec.pool = {
          el: 'lava',
          radius: LAVA_POOL.baseRadius + LAVA_POOL.radiusPerLava * (nLava - 1),
          lifetime: LAVA_POOL.baseLifetime + LAVA_POOL.lifetimePerLava * nLava,
          perTick: { ...emptyPacket(), fire: LAVA_POOL.firePerTick * nLava, statuses: ['burning'], burn: nLava },
          tickSeconds: LAVA_POOL.tickSeconds,
        };
      }
      return spec;
    }
    case 'ice': {
      const nIce = count(counts, 'ice');
      const payload = addPackets(statusesOnly(elementPacket('payload', 'ice', nIce)), sumPackets('payload', counts, ['ice']));
      return {
        kind: 'projectile', solid: 'ice', shards: SHARDS.perIce * nIce, direct: SHARDS.damage, payload,
        blastRadius: 0, speed: SHARDS.speedMin, range: SHARDS.range, elements,
      };
    }
    case 'life':
    case 'arcane': {
      const nBeam = count(counts, 'life') + count(counts, 'arcane');
      return {
        kind: 'beam', ticks: BEAM.baseTicks + BEAM.ticksPerExtraBeamElement * (nBeam - 1),
        perTick: sumPackets('beam', counts), knockback: WATER_KNOCKBACK.beam * nWater, elements,
      };
    }
    case 'lightning':
      return { kind: 'arc', ticks: ARC.ticks, perTick: sumPackets('arc', counts), range: ARC.range, chain: 1 + count(counts, 'lightning'), elements };
    default: {
      let perTick = sumPackets('spray', counts);
      // DESIGN 2.5 / 3 #7: Lightning in a spray doubles the whole packet; baked in so the preview shows the real number.
      if (count(counts, 'lightning') > 0) perTick = scalePacket(perTick, SPRAY_LIGHTNING_MUL);
      return {
        kind: 'spray', ticks: SPRAY.baseTicks + SPRAY.ticksPerElement * elements.length, perTick,
        range: SPRAY.range + SPRAY.waterRangeBonus * nWater, halfAngle: SPRAY.halfAngleDeg * DEG,
        knockback: WATER_KNOCKBACK.spray * nWater, elements,
      };
    }
  }
}

// ---------------------------------------------------------------- Area (DESIGN 2.4 column 3)

function resolveArea(dom: Element, counts: ElementCounts, elements: Element[]): SpellSpec {
  if (dom === 'earth' || dom === 'lava') {
    // Lava counts as earth for the quake; its fire is in the payload, so a lava quake sets the floor alight.
    const nEarth = count(counts, 'earth') + count(counts, 'lava');
    // DESIGN 2.5 Quake: knockdown, 0 earth damage; the payload lands on everyone in the radius.
    const payload = addPackets(statusesOnly(elementPacket('payload', 'earth', nEarth)), sumPackets('payload', counts, ['earth']));
    return { kind: 'quake', radius: QUAKE.baseRadius + nEarth, payload };
  }
  return {
    kind: 'nova', radius: NOVA.baseRadius + NOVA.radiusPerExtraElement * (elements.length - 1),
    packet: sumPackets('nova', counts), knockback: WATER_KNOCKBACK.nova * count(counts, 'water'), elements,
  };
}

// ---------------------------------------------------------------- Self (DESIGN 2.6)

function resolveSelf(dom: Element, counts: ElementCounts): SpellSpec {
  const n = count(counts, dom);
  // DESIGN 2.6: payload elements apply their status and projectile-payload damage to the caster.
  const payload = sumPackets('payload', counts, [dom]);
  if (dom === 'life') {
    return { kind: 'self', packet: payload, heal: SELF_CAST.heal, hotTicks: n - 1, knockdown: false };
  }
  let own: DamagePacket;
  switch (dom) {
    case 'fire':
    case 'water':
    case 'cold':
      // Status only, no damage (DESIGN 2.6).
      own = statusesOnly(elementPacket('payload', dom, n));
      break;
    case 'lightning':
      // DESIGN 2.6 uses the nova number (250 s(n)) for self-lightning.
      own = elementPacket('nova', 'lightning', n);
      break;
    default:
      // steam 280 s(n), arcane 425, earth 75 T(n), ice 137.5 n^2: the payload column.
      own = elementPacket('payload', dom, n);
  }
  const packet = addPackets(own, payload);
  // DESIGN 2.6 "1 stack per Fire": one burning entry per Fire, appended after the dedupe in addPackets.
  if (dom === 'fire') for (let i = 1; i < n; i++) packet.statuses.push('burning');
  return { kind: 'self', packet, heal: 0, hotTicks: 0, knockdown: hasKnockdown(packet) };
}

// ---------------------------------------------------------------- Weapon (DESIGN 2.4 column 5, 2.5 Melee row)

function resolveWeapon(dom: Element, counts: ElementCounts): SpellSpec {
  switch (dom) {
    case 'earth': {
      // DESIGN 2.5: fissure = knockdown, 0 damage, plus the payload.
      const packet = addPackets(statusesOnly(elementPacket('payload', 'earth', count(counts, 'earth'))), sumPackets('payload', counts, ['earth']));
      return { kind: 'melee', shape: 'fissure', packet, knockdown: true };
    }
    case 'ice': {
      // DESIGN 2.5: the Ice slash is the blade itself (180 s(n) physical), so no 100 staff base.
      const nIce = count(counts, 'ice');
      const blade: DamagePacket = { ...statusesOnly(elementPacket('payload', 'ice', nIce)), physical: MELEE.iceSlashBase * stackFluid(nIce) };
      const packet = addPackets(blade, sumPackets('payload', counts, ['ice']));
      return { kind: 'melee', shape: 'slash', packet, knockdown: hasKnockdown(packet) };
    }
    case 'life':
    case 'arcane': {
      const packet = addPackets(meleeBase(), sumPackets('payload', counts));
      return { kind: 'melee', shape: 'swing', packet, knockdown: hasKnockdown(packet) };
    }
    default: {
      const packet = addPackets(meleeBase(), sumPackets('payload', counts));
      return { kind: 'melee', shape: 'thrust', packet, knockdown: hasKnockdown(packet) };
    }
  }
}

// ---------------------------------------------------------------- woven pieces (docs/spells-depth.md 1.4)

/**
 * Each field piece's zone: how far ahead, how big, how long, and the time
 * scale inside. A `speed` rolls it away along the aim instead (it travels
 * speed x lifetime).
 */
const FIELDS: Readonly<Partial<Record<WovenElement, { sig: FieldSig; dist: number; radius: number; lifetime: number; scale: number; speed?: number }>>> = {
  stillness:   { sig: 'stasis', dist: 5, radius: 3, lifetime: 4, scale: 0.4 },
  cloud:       { sig: 'rain', dist: 6, radius: 3, lifetime: 8, scale: 1 },
  moment:      { sig: 'stop', dist: 5, radius: 3, lifetime: 2, scale: 0.02 },
  thunderhead: { sig: 'thunder', dist: 7, radius: 3.5, lifetime: 8, scale: 1 },
  time:        { sig: 'slow', dist: 6, radius: 4, lifetime: 6, scale: 0.3 },
  // slice 2
  tide:        { sig: 'wave', dist: 1, radius: 1.7, lifetime: 1.15, scale: 1, speed: 9 },
  void:        { sig: 'orb', dist: 1, radius: 1.1, lifetime: 3, scale: 1, speed: 4 },
  glacier:     { sig: 'roll', dist: 1.3, radius: 1.4, lifetime: 1.5, scale: 1, speed: 8 },
  whirlpool:   { sig: 'pull', dist: 6, radius: 3.2, lifetime: 4, scale: 1 },
  // its near edge stops 2 u short of the caster: a dry caster can then send Lightning through it
  deluge:      { sig: 'flood', dist: 7.5, radius: 5.5, lifetime: 6, scale: 1 },
  winter:      { sig: 'winter', dist: 7, radius: 6, lifetime: 8, scale: 1 },
};

/** Field signatures whose pulse carries their piece's statuses (the time fields work by their scale alone). */
const CARRYING: ReadonlySet<FieldSig> = new Set<FieldSig>(['rain', 'thunder', 'pull', 'flood', 'winter']);

/** An Area cast of a field piece puts it on the caster, a quarter shorter. */
const AREA_FIELD_LIFETIME = 0.75;

const FORCE_SIG: Readonly<Partial<Record<WovenElement, Sig>>> = {
  flux: 'drag', ether: 'phase', dragonfire: 'scorch', rime: 'rime', plasma: 'pop', obsidian: 'shatter', stormglass: 'web',
  wildfire: 'spread', inferno: 'inferno', soul: 'soul',
};

/** Pulse's arc reaches this many bodies in all (the first and three hops). */
export const PULSE_CHAIN = 3;
/** Second Wind (Pulse on yourself) and Soul Ward: the killing blow they turn, how long they wait, what they leave you on. */
export const WARDS = { pulse: { t: 6, hp: 1 }, soul: { t: 10, hp: 300 } } as const;
/** Self casts that are armor: Glacier's and Winter's. */
export const COLD_ARMOR = { glacier: { absorb: 3000, slow: 0.4 }, winter: { absorb: 4000, slow: 0.3 } } as const;
/** The rings that throw everyone out: Tide's (three Waters' push), Whirlpool's, and Whirlpool on yourself (a spin, smaller). */
const THROW = { tide: 18, whirlpool: 15, spinRadius: 2.5 } as const;
/** Inferno on yourself: a ring of fire at your feet that burns everyone in it, you a little. */
export const BLAZE = { radius: 2.2, lifetime: 6, fire: 60 } as const;
/** Sun: the burst's place and size; its Area cast is the same burst round you. */
export const SUNBURST = { dist: 6, radius: 4 } as const;
/** Glacier's Area cast: a quake that freezes. */
const AVALANCHE_RADIUS = 5;

/** Glass armor (Self with Obsidian or Storm Glass): absorbs this much, no slow. */
export const GLASS_ARMOR = { obsidian: 2000, stormglass: 2500 } as const;

/** Moment's Self cast: a blink this far along the aim. */
export const BLINK_DISTANCE = 5;

/** Pieces that bring a beam's length up, like Life and Arcane. */
function beamCount(counts: ElementCounts): number {
  return count(counts, 'life') + count(counts, 'arcane') + count(counts, 'flux') + count(counts, 'ether') + count(counts, 'dragonfire');
}

function resolveWoven(dom: WovenElement, mode: CastMode, counts: ElementCounts, elements: Element[]): SpellSpec {
  if (isConcept(dom) && mode !== 'weapon') return resolveConcept(dom, mode, counts, elements);
  switch (mode) {
    case 'force': return wovenForce(dom, counts, elements);
    case 'area': return wovenArea(dom, counts, elements);
    case 'self': return wovenSelf(dom, counts);
    case 'weapon': return resolveWeapon(dom, counts);
  }
}

function fieldSpec(dom: WovenElement, counts: ElementCounts, elements: Element[], onCaster: boolean): SpellSpec {
  const f = FIELDS[dom];
  if (!f) return { kind: 'none' };
  const n = count(counts, dom);
  if (f.speed !== undefined) {
    // A rolling field lands its piece's payload (and the rest of the queue's) once on each body it reaches.
    const hit = addPackets(elementPacket('payload', dom, n), sumPackets('payload', counts, [dom]));
    return { kind: 'field', sig: f.sig, dist: f.dist, radius: f.radius, lifetime: f.lifetime, scale: 1, perTick: emptyPacket(), elements, speed: f.speed, hit };
  }
  const own = CARRYING.has(f.sig) ? statusesOnly(elementPacket('spray', dom, n)) : emptyPacket();
  const perTick = addPackets(own, sumPackets('spray', counts, [dom]));
  return {
    kind: 'field', sig: f.sig, dist: onCaster ? 0 : f.dist, radius: f.radius,
    lifetime: onCaster ? f.lifetime * AREA_FIELD_LIFETIME : f.lifetime, scale: f.scale, perTick, elements,
  };
}

/** A nova of the piece round the caster (never the caster). */
function novaOf(counts: ElementCounts, elements: Element[], extra: { radius?: number; knockback?: number; sig?: Sig } = {}): SpellSpec {
  return {
    kind: 'nova', radius: extra.radius ?? NOVA.baseRadius + NOVA.radiusPerExtraElement * (elements.length - 1),
    packet: sumPackets('nova', counts), knockback: extra.knockback ?? WATER_KNOCKBACK.nova * count(counts, 'water'), elements,
    ...(extra.sig ? { sig: extra.sig } : {}),
  };
}

/**
 * Concepts invoke (docs/spells-depth.md 2.6): Force aims them, Area centres them on
 * you, Self puts them on you. Everything else in the queue rides along.
 */
function resolveConcept(dom: ConceptElement, mode: Exclude<CastMode, 'weapon'>, counts: ElementCounts, elements: Element[]): SpellSpec {
  switch (dom) {
    case 'sun': {
      if (mode === 'self') return wovenSelf(dom, counts);
      const packet = sumPackets('nova', counts);
      if (mode === 'area') return { kind: 'nova', radius: SUNBURST.radius, packet, knockback: 0, elements };
      return { kind: 'burst', dist: SUNBURST.dist, radius: SUNBURST.radius, packet, elements };
    }
    case 'winter':
      if (mode === 'self') return wovenSelf(dom, counts);
      return fieldSpec(dom, counts, elements, mode === 'area');
    case 'death': {
      const reach = mode === 'force' ? 'aim' : mode === 'area' ? 'around' : 'self';
      return { kind: 'mark', reach, range: reach === 'aim' ? DEATH_MARK.range : DEATH_MARK.radius, frac: DEATH_MARK.frac, bossFrac: DEATH_MARK.bossFrac, delay: DEATH_MARK.delay };
    }
  }
}

function wovenForce(dom: WovenElement, counts: ElementCounts, elements: Element[]): SpellSpec {
  const nWater = count(counts, 'water');
  const sig = FORCE_SIG[dom];
  switch (FORM_OF[dom]) {
    case 'field': return fieldSpec(dom, counts, elements, false);
    case 'projectile': {
      // A glass bolt: all the physical is the direct hit; everything else rides the blast like a boulder's payload.
      const payload = sumPackets('payload', counts);
      const beamBonus = count(counts, 'arcane') + count(counts, 'life') > 0 ? BOULDER.beamElementBlastBonus : 0;
      return {
        kind: 'projectile', solid: 'earth', shards: 0, direct: payload.physical, payload: { ...payload, physical: 0 },
        blastRadius: BOULDER.blastRadius + BOULDER.waterBlastBonus * nWater + beamBonus, speed: BOULDER.speed, range: BOULDER.range, elements,
        ...(sig ? { sig } : {}),
      };
    }
    case 'beam':
      return {
        kind: 'beam', ticks: BEAM.baseTicks + BEAM.ticksPerExtraBeamElement * (beamCount(counts) - 1),
        perTick: sumPackets('beam', counts), knockback: WATER_KNOCKBACK.beam * nWater, elements, ...(sig ? { sig } : {}),
      };
    case 'arc':
      return {
        kind: 'arc', ticks: ARC.ticks, perTick: sumPackets('arc', counts), range: ARC.range,
        // Pulse's heartbeat runs through four bodies; Plasma chains like Lightning.
        chain: dom === 'pulse' ? PULSE_CHAIN + count(counts, 'lightning') : 1 + count(counts, 'lightning') + count(counts, 'plasma'),
        elements, ...(sig ? { sig } : {}),
      };
    default: {
      let perTick = sumPackets('spray', counts);
      if (count(counts, 'lightning') > 0) perTick = scalePacket(perTick, SPRAY_LIGHTNING_MUL);
      return {
        kind: 'spray', ticks: SPRAY.baseTicks + SPRAY.ticksPerElement * elements.length, perTick,
        range: SPRAY.range + SPRAY.waterRangeBonus * nWater, halfAngle: SPRAY.halfAngleDeg * DEG,
        knockback: WATER_KNOCKBACK.spray * nWater, elements, ...(sig ? { sig } : {}),
      };
    }
  }
}

function wovenArea(dom: WovenElement, counts: ElementCounts, elements: Element[]): SpellSpec {
  switch (dom) {
    // The sea throws everyone out; Void unmakes what's round you; a Glacier quakes and freezes.
    case 'tide': return novaOf(counts, elements, { knockback: THROW.tide });
    case 'whirlpool': return novaOf(counts, elements, { knockback: THROW.whirlpool, radius: 3.5 });
    case 'void': return novaOf(counts, elements, { radius: 4, sig: 'unmake' });
    case 'glacier': {
      const payload = addPackets(elementPacket('nova', 'glacier', count(counts, 'glacier')), sumPackets('payload', counts, ['glacier']));
      return { kind: 'quake', radius: AVALANCHE_RADIUS, payload, sig: 'rime' };
    }
    case 'pulse': return novaOf(counts, elements, { sig: 'thaw' });
    case 'soul': return novaOf(counts, elements, { radius: 4, sig: 'allies' });
    case 'wildfire': return novaOf(counts, elements, { sig: 'spread' });
    case 'inferno': return novaOf(counts, elements, { radius: 4, sig: 'spread' });
    default: break;
  }
  if (FIELDS[dom]) return fieldSpec(dom, counts, elements, true);
  // Every other piece: a nova of itself; Rime's freezes the dry too.
  return novaOf(counts, elements, dom === 'rime' ? { sig: 'rime' } : {});
}

function wovenSelf(dom: WovenElement, counts: ElementCounts): SpellSpec {
  switch (dom) {
    case 'tide':
      // Soak: wet through, put out, the mud washed off.
      return { kind: 'self', packet: { ...emptyPacket(), statuses: ['wet'] }, heal: 0, hotTicks: 0, knockdown: false, cleanse: ['mired', 'burning'] };
    case 'void':
      return { kind: 'self', packet: emptyPacket(), heal: 0, hotTicks: 0, knockdown: false, cleanse: 'all' };
    case 'pulse':
    case 'soul':
      return { kind: 'self', packet: emptyPacket(), heal: 0, hotTicks: 0, knockdown: false, ward: { ...WARDS[dom] } };
    case 'wildfire':
      // Wreathed: a ring where fire can't touch you.
      return { kind: 'aura', immune: ['fire'], radius: 2, lifetime: 5 };
    case 'whirlpool': {
      // A spin: everyone close is thrown out and soaked.
      return { kind: 'nova', radius: THROW.spinRadius, packet: sumPackets('nova', counts), knockback: THROW.whirlpool, elements: ['whirlpool'] };
    }
    case 'inferno':
      return {
        kind: 'field', sig: 'blaze', dist: 0, radius: BLAZE.radius, lifetime: BLAZE.lifetime, scale: 1,
        perTick: { ...emptyPacket(), fire: BLAZE.fire, statuses: ['burning'], burn: 1 }, elements: ['inferno'],
      };
    case 'glacier':
    case 'winter':
      return { kind: 'armor', solid: 'ice', absorb: COLD_ARMOR[dom].absorb, slow: COLD_ARMOR[dom].slow, lifetime: ARMOR.lifetime };
    case 'obsidian':
    case 'stormglass':
      return { kind: 'armor', solid: 'earth', absorb: GLASS_ARMOR[dom], slow: 0, lifetime: ARMOR.lifetime };
    case 'rime':
      return { kind: 'armor', solid: 'ice', absorb: ARMOR.iceAbsorb, slow: ARMOR.iceSlow, lifetime: ARMOR.lifetime };
    case 'moment':
      return { kind: 'blink', dist: BLINK_DISTANCE };
    case 'stillness':
      return { kind: 'bubble', hp: BUBBLE.hp * 2 };
    case 'flux':
    case 'time': {
      const packet = addPackets({ ...emptyPacket(), statuses: ['hasted'] }, statusesOnly(sumPackets('payload', counts, [dom])));
      return { kind: 'self', packet, heal: 0, hotTicks: 0, knockdown: false };
    }
    default: {
      // The cruel ones: their statuses (and the rest of the queue's) on you, without the damage.
      const packet = statusesOnly(addPackets(elementPacket('payload', dom, count(counts, dom)), sumPackets('payload', counts, [dom])));
      if (packet.statuses.includes('burning')) packet.burn = 1;
      return { kind: 'self', packet, heal: 0, hotTicks: 0, knockdown: hasKnockdown(packet) };
    }
  }
}

// ---------------------------------------------------------------- names (DESIGN 6.5, 9)


const ELEMENT_NAME: Readonly<Record<Element, string>> = {
  water: 'Water', life: 'Life', shield: 'Shield', cold: 'Cold', lightning: 'Lightning', arcane: 'Arcane', earth: 'Earth', fire: 'Fire', steam: 'Steam', ice: 'Ice', mud: 'Mud', lava: 'Lava', spring: 'Spring',
  plasma: 'Plasma', flux: 'Flux', stillness: 'Stillness', obsidian: 'Obsidian', ether: 'Ether', cloud: 'Cloud', ash: 'Ash', rime: 'Rime', moment: 'Moment',
  dragonfire: 'Dragonfire', stormglass: 'Storm Glass', thunderhead: 'Thunderhead', time: 'Time',
  tide: 'Tide', pulse: 'Pulse', void: 'Void', wildfire: 'Wildfire', whirlpool: 'Whirlpool', deluge: 'Deluge', inferno: 'Inferno',
  glacier: 'Glacier', soul: 'Soul', sun: 'Sun', winter: 'Winter', death: 'Death',
};

/** Display name of an element or woven piece. */
export function elementName(el: Element): string {
  return ELEMENT_NAME[el];
}

/** Curated names keyed by `kind:` + the unique elements in keyboard order joined with `+`. */
const CURATED: Readonly<Record<string, string>> = {
  'spray:mud': 'Mud Spray',
  'nova:mud': 'Mudslide',
  'storm:mud': 'Swamp',
  'self:mud': 'Mud Bath',
  'aura:mud': 'Mud Ward',
  'projectile:lava': 'Lava Boulder',
  'quake:lava': 'Eruption',
  'wall:earth+lava': 'Volcano Wall',
  'storm:lava': 'Ring of Fire',
  'self:lava': 'Lava Bath (ouch)',
  'spray:spring': 'Healing Mist',
  'nova:spring': 'Healing Spring',
  'storm:spring': 'Healing Rain',
  'self:spring': 'Spring Bath',
  'beam:arcane': 'Arcane Beam',
  'beam:life': 'Healing Beam',
  'beam:lightning+arcane+steam': 'Steam-Lightning Beam',
  'spray:fire': 'Fire Spray',
  'spray:cold': 'Frost Spray',
  'spray:water': 'Water Spray',
  'spray:steam': 'Steam Spray',
  'arc:lightning': 'Lightning Arc',
  'arc:lightning+fire': 'Igniting Arc',
  'arc:cold+lightning': 'Chilling Arc',
  'projectile:earth': 'Boulder',
  'projectile:earth+fire': 'Fireball',
  'projectile:earth+ice': 'Hailstone Boulder',
  'projectile:earth+steam': 'Steamball',
  'projectile:arcane+earth+fire+steam': 'Arcane Steamball',
  'projectile:ice': 'Ice Barrage',
  'nova:lightning': 'Lightning Nova',
  'nova:life': 'Healing Nova',
  'nova:ice': 'Ice Spikes Nova',
  'quake:': 'Quake',
  'wall:earth+fire': 'Volcano Wall',
  'wall:lightning+ice': 'Tesla Wall',
  'wall:earth': 'Stone Wall',
  'wall:ice': 'Ice Wall',
  'mines:arcane': 'Arcane Mines',
  'mines:life': 'Healing Mines',
  'storm:water': 'Rain Storm',
  'storm:cold': 'Frost Storm',
  'self:fire': 'Self-Ignite',
  'self:water': 'Self-Douse',
  'self:cold': 'Self-Chill',
  'melee:': 'Staff Swing',
  'melee:earth': 'Fissure',
  'melee:ice': 'Ice Slash',
  // Woven pieces (docs/spells-depth.md 2).
  'arc:plasma': 'Plasma Arc',
  'beam:flux': 'Flux Beam',
  'field:stillness': 'Stasis Field',
  'projectile:obsidian': 'Obsidian Bolt',
  'beam:ether': 'Ether Beam',
  'field:cloud': 'Raincloud',
  'spray:ash': 'Ash Cloud',
  'spray:rime': 'Rime Spray',
  'nova:rime': 'Rime Nova',
  'field:moment': 'Frozen Moment',
  'beam:dragonfire': "Dragon's Breath",
  'projectile:stormglass': 'Storm Glass Bolt',
  'field:thunderhead': 'Thunderhead',
  'field:time': 'Hourglass',
  'armor:obsidian': 'Obsidian Armor',
  'armor:stormglass': 'Storm Armor',
  'armor:rime': 'Rime Armor',
  'blink:moment': 'Blink',
  'bubble:stillness': 'Stasis',
  'self:flux': 'Flux Haste',
  'self:time': 'Quickened',
  // slice 2
  'field:tide': 'Tidal Wave',
  'nova:tide': 'Tidal Ring',
  'self:tide': 'Soak',
  'arc:pulse': 'Pulse',
  'nova:pulse': 'Heartbeat',
  'self:pulse': 'Second Wind',
  'mines:pulse': 'Pulse Mines',
  'field:void': 'Void Orb',
  'nova:void': 'Unmaking',
  'self:void': 'Cleanse',
  'barrier:void': 'Void Wall',
  'spray:wildfire': 'Wildfire',
  'nova:wildfire': 'Wildfire Nova',
  'aura:wildfire': 'Wreathed',
  'field:whirlpool': 'Whirlpool',
  'nova:whirlpool': 'Riptide',
  'field:deluge': 'Deluge',
  'self:deluge': 'Drenched',
  'spray:inferno': 'Inferno',
  'nova:inferno': 'Firestorm',
  'field:inferno': 'Inferno Aura',
  'field:glacier': 'Glacier',
  'quake:glacier': 'Avalanche',
  'armor:glacier': 'Glacier Armor',
  'wall:ice+glacier': 'Glacier Wall',
  'beam:soul': 'Soul Beam',
  'nova:soul': 'Soul Light',
  'self:soul': 'Soul Ward',
  'mines:soul': 'Soul Mines',
  'burst:sun': 'Sunburst',
  'nova:sun': 'Solar Flare',
  'self:sun': 'Sun Bath (ouch)',
  'field:winter': 'Winter',
  'armor:winter': "Winter's Coat",
  'mark:death': 'Death Mark',
};

function unique(els: readonly Element[]): Element[] {
  return sortedElements(els.filter((el, i) => els.indexOf(el) === i));
}

/** "Arcane-Steam-Lightning": dominant first, then the rest in keyboard order. */
function joinNames(els: readonly Element[]): string {
  const list = unique(els);
  const dom = dominant(list);
  const ordered = dom === null ? list : [dom, ...list.filter((el) => el !== dom)];
  return ordered.map((el) => ELEMENT_NAME[el]).join('-');
}

/** Elements that identify a spec: its queue, or what its packet carries plus any element implied by the form. */
function nameElements(spec: SpellSpec): Element[] {
  switch (spec.kind) {
    case 'beam': case 'spray': case 'arc': case 'projectile': case 'nova': return unique(spec.elements);
    case 'quake': return packetElements(spec.payload, 'ice').filter((el) => el !== 'earth');
    case 'wall': return unique([spec.solid, ...packetElements(spec.rise, spec.solid)]);
    case 'mines': return packetElements(spec.packet, null);
    case 'storm': return packetElements(spec.perTick, null);
    case 'self': {
      if (spec.heal > 0) return unique(['life', ...packetElements(spec.packet, 'ice')]);
      return packetElements(spec.packet, hasKnockdown(spec.packet) ? 'earth' : 'ice');
    }
    case 'melee': {
      // The blade's own physical damage is the shape's element; the 100 staff base of swing/thrust is ignored.
      const physicalAs = spec.shape === 'slash' ? 'ice' : spec.shape === 'fissure' ? 'earth' : null;
      const carried = packetElements(spec.packet, physicalAs);
      if (spec.shape === 'fissure') return unique(['earth', ...carried]);
      if (spec.shape === 'slash') return unique(['ice', ...carried]);
      return carried;
    }
    case 'aura': return unique(spec.immune);
    default: return [];
  }
}

/** Element list for naming a fusion spec straight from its queue. */
function queueNameElements(spec: SpellSpec, queue: readonly Element[]): Element[] {
  const els = unique(queue.filter((el) => el !== 'shield'));
  switch (spec.kind) {
    case 'quake': return els.filter((el) => el !== 'earth');
    case 'wall': return unique([spec.solid, ...els.filter((el) => el !== spec.solid)]);
    default: return els;
  }
}

/** Generic "<Elements> <Form>" fallback; the noun already names the dominant element where the form implies it. */
function genericName(spec: SpellSpec, els: readonly Element[]): string {
  const prefix = (noun: string, list: readonly Element[] = els): string => (list.length > 0 ? `${joinNames(list)} ${noun}` : noun);
  switch (spec.kind) {
    case 'beam': return prefix('Beam');
    case 'spray': return prefix('Spray');
    case 'arc': return prefix('Arc');
    case 'projectile': return spec.solid === 'earth' ? prefix('Boulder', els.filter((el) => el !== 'earth')) : prefix('Ice Barrage', els.filter((el) => el !== 'ice'));
    case 'nova': return prefix('Nova');
    case 'quake': return prefix('Quake');
    case 'barrier': return spec.geometry === 'arc' ? 'Barrier Arc' : spec.geometry === 'ring' ? 'Barrier Dome' : 'Barrier Line';
    case 'wall': return prefix(spec.solid === 'earth' ? 'Stone Wall' : 'Ice Wall', els.filter((el) => el !== spec.solid));
    case 'mines': return prefix(spec.packet.life > 0 ? 'Healing Mines' : 'Arcane Mines', els.filter((el) => el !== 'arcane' && el !== 'life'));
    case 'storm': return prefix('Storm');
    case 'self': return spec.heal > 0 ? prefix(spec.hotTicks > 0 ? 'Regenerating Heal' : 'Heal', els.filter((el) => el !== 'life')) : prefix('Self-Cast');
    case 'bubble': return 'Personal Bubble';
    case 'aura': return prefix('Resistance Aura');
    case 'armor': return spec.solid === 'earth' ? 'Rock Armor' : 'Ice Armor';
    case 'melee':
      switch (spec.shape) {
        case 'swing': return prefix('Imbued Swing');
        case 'thrust': return prefix('Thrust');
        case 'fissure': return prefix('Fissure', els.filter((el) => el !== 'earth'));
        case 'slash': return prefix('Ice Slash', els.filter((el) => el !== 'ice'));
      }
      break;
    case 'wind': return spec.geometry === 'cone' ? 'Wind Blast' : 'Wind Nova';
    case 'field': return prefix('Field');
    case 'burst': return prefix('Burst');
    case 'mark': return 'Death Mark';
    case 'blink': return 'Blink';
    case 'none': return 'Nothing';
  }
  return 'Spell';
}

/** Human-readable name for the HUD preview, e.g. "Steam-Lightning Beam" (DESIGN 6.5, 9). */
export function spellName(spec: SpellSpec): string {
  const fromQueue = QUEUE_OF.get(spec);
  const els = fromQueue ? queueNameElements(spec, fromQueue) : nameElements(spec);
  return CURATED[`${spec.kind}:${els.join('+')}`] ?? genericName(spec, els);
}
